import {
  useInteractions,
  useRole,
  useDismiss,
  useClick,
  FloatingFocusManager,
  FloatingNode,
  FloatingPortal,
  FloatingTree,
  useFloatingParentNodeId,
  useHover,
  safePolygon,
  Strategy,
} from '@floating-ui/react';
import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React, {
  cloneElement,
  isValidElement,
  LegacyRef,
  MutableRefObject,
  ReactNode,
  useState,
  CSSProperties,
  HTMLAttributes,
} from 'react';

import {assertAndContinue} from 'Asserts';
import {ZIndexRole} from 'styles/StyleConstants';
import getTestProps from 'test/lib/cypress/getTestProps';
import DataTestAttrs from 'test/lib/cypress/shared/DataTestAttrs';
import {Title} from 'ui-library/Headers';
import stopPropagation from 'util/stopPropagation';

import {
  CONTENT_CLASSNAME,
  FloatingInteraction,
  FloatingPlacement,
  RenderContentProps,
  TOGGLE_CLASSNAME,
} from './FloatingConstants';
import useFloating, {ArrowProps, FloatingPlacementOptions, FloatingProps} from './useFloating';

/**
 * Determines if an element is a primitive DOM element, for example <span> or <li>
 *
 * See https://reactjs.org/docs/jsx-in-depth.html#user-defined-components-must-be-capitalized for more details
 * on why this works.
 */
function isDOMTypeElement(element: JSX.Element): boolean {
  return isValidElement(element) && typeof element.type === 'string';
}

export enum ToggleDisplayMode {
  INLINE = 'INLINE',
  BLOCK_FIT_CONTENT = 'BLOCK_FIT_CONTENT',
  BLOCK = 'BLOCK',
}

interface ToggleProps {
  children: JSX.Element;
  /** Used for merging props to the "reference" element, which is what @floating-ui calls the toggle. See
   * https://floating-ui.com/docs/react-dom-interactions#prop-getters. Not relevant for controlled
   * float interactions, because it's not necessary to merge in the event listener props. */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getReferenceProps?: (userProps?: React.HTMLProps<Element> | undefined) => any;
  toggleRef: LegacyRef<HTMLElement>;
  /**
   * Whether the element wrapping the toggle should be inline or block.
   *
   * @default ToggleDisplayMode.INLINE
   */
  displayMode?: ToggleDisplayMode;
  floatingNodeId: string | undefined;
}

/**
 * Wrapper component around the reference element (toggle) of the floating system.
 */
function Toggle({
  children,
  displayMode = ToggleDisplayMode.INLINE,
  floatingNodeId,
  getReferenceProps,
  toggleRef,
}: ToggleProps): JSX.Element {
  // Starting point from which to merge in reference props like event listeners. At a minimum needs access to
  // the toggle reference.
  const userProps = {ref: toggleRef};
  const referenceProps = getReferenceProps != null ? getReferenceProps(userProps) : userProps;

  if (isDOMTypeElement(children)) {
    assertAndContinue(
      referenceProps.className == null,
      "Floating UI: Didn't expect to find 'className' in reference element props"
    );
    return cloneElement(children, {
      'aria-describedby': floatingNodeId,
      className: classNames(children.props.className, TOGGLE_CLASSNAME),
      ...referenceProps,
    });
  } else {
    return (
      <div
        aria-describedby={floatingNodeId}
        className={classNames(TOGGLE_CLASSNAME, css(styles.floatingToggle), {
          [css(styles.inlineToggle)]: displayMode === ToggleDisplayMode.INLINE,
          [css(styles.blockToggle)]: displayMode === ToggleDisplayMode.BLOCK,
          [css(styles.blockFitContentToggle)]: displayMode === ToggleDisplayMode.BLOCK_FIT_CONTENT,
          // Temporary repetition of Aphrodite styles with Less to fix inconsistency between print preview
          // (UI) and exported layout (BNCH-105596). This should be removed once aphrodite support for
          // nodeshell is added (BNCH-97400) or nodeshell gets retired.
          'inline-toggle': displayMode === ToggleDisplayMode.INLINE,
          'block-toggle': displayMode === ToggleDisplayMode.BLOCK,
          'block-fit-toggle': displayMode === ToggleDisplayMode.BLOCK_FIT_CONTENT,
        })}
        {...referenceProps}
      >
        {children}
      </div>
    );
  }
}

export interface EventPropagationOptions {
  /**
   * If true, allows mousedown events to propagate from the floating element
   */
  permitOnMouseDown?: boolean;
  /**
   * If true, allows dblclick events to propagate from the floating element
   */
  permitOnDoubleClick?: boolean;
  /**
   * If true, allows contextmenu events to propagate from the floating element
   */
  permitOnContextMenu?: boolean;
}

/** Options for additional configuration of the floating element. Used by Popover, for example, to configure
 * speech bubble styling. */
export interface FloatingElementOptions {
  /** renderArrow is a render prop which, if present, supplies an element to be placed as a sibling of the
   * floating content. Typically, this element is an indicator which points from the body of the floating
   * element to the toggle element. */
  renderArrow?: React.FC<ArrowProps>;
  /** floatingBodyClassName is used for injecting styles on the floating body element (like a border, for
   * example). Floating body is the parent element of the floating content element. */
  floatingBodyClassName?: string;
  /**
   * zIndex of the floating body element.
   *
   * @default ZIndexRole.TOOLTIP
   * */
  floatingBodyZIndex?: ZIndexRole.MODAL | ZIndexRole.TOOLTIP;
  /** floatingContentClassName is used for injecting styles on the floating content (like a max content width, for
   * example). Floating content is the child element of the floating body. */
  floatingContentClassName?: string;
  /** floatingContentStyles is used for injecting dynamic styles on the floating content that cannot be applied
   * by a static stylesheet (like a size that depends on component parameters, for example). Floating content is
   * the child element of the floating body. */
  floatingContentStyles?: CSSProperties;
  /**
   * eventPropagation is used to control whether events propagate from the floating element.
   */
  eventPropagationOptions?: EventPropagationOptions;
  dataTestAttrs?: DataTestAttrs;
  /**
   * When closed, will return focus to whatever element had focus when the Floating element was opened.
   * This is true by default, and will be disabled if set false
   */
  returnFocusOnClose?: boolean;
}

interface FloatingElementProps {
  arrowProps: ArrowProps;
  floatingProps: FloatingProps;
  floatingOptions?: FloatingElementOptions;
  renderContent: (data: RenderContentProps) => ReactNode;
  /**
   * ARIA role for the outermost floating element (i.e. the immediate child of the portal). If this float has
   * any interactivity within the floating content, this must be overridden.
   * @default tooltip
   */
  role?: string;
  strategy: Strategy;
  title?: string;
  disableContentSelection?: boolean;
  stealFocus?: boolean;
}

/**
 * Responsible for the floating portion of the popover. Note that CSS classes are not intended to be used for
 * styling, but are useful for visualizing the component in the DOM (as well as for test selection).
 */
function FloatingElement({
  arrowProps,
  floatingProps,
  floatingOptions,
  renderContent,
  role = 'tooltip',
  strategy,
  title,
  disableContentSelection,
  stealFocus = true,
}: FloatingElementProps): JSX.Element {
  const {floatingContext, floatingRef, floatingPos, handleClose} = floatingProps;

  const labelId = `${floatingProps.floatingNodeId}-label`;
  const descriptionId = `${floatingProps.floatingNodeId}-description`;

  const ariaProps = {
    'aria-labelledby': labelId,
    'aria-describedby': descriptionId,
  };

  const testAttrs: DataTestAttrs = floatingOptions?.dataTestAttrs ?? {
    component: 'Float',
    element: 'floatingContent',
  };

  return (
    <FloatingFocusManager
      context={floatingContext}
      returnFocus={floatingOptions?.returnFocusOnClose}
      disabled={disableContentSelection || !stealFocus}
    >
      <div
        className={classNames(
          'floating-ui-body',
          css(styles.floatingBody),
          floatingOptions?.floatingBodyClassName,
          {
            'u-no-select': disableContentSelection,
            'u-no-mouse-target': disableContentSelection,
          }
        )}
        id={floatingProps.floatingNodeId}
        ref={floatingRef as MutableRefObject<HTMLDivElement>}
        role={role}
        style={{
          position: strategy,
          top: floatingPos.y ?? '',
          left: floatingPos.x ?? '',
          zIndex: floatingOptions?.floatingBodyZIndex ?? ZIndexRole.TOOLTIP,
        }}
        {...ariaProps}
      >
        <div
          className={classNames(
            CONTENT_CLASSNAME,
            'scrollbar-override',
            css(styles.floatingContent),
            floatingOptions?.floatingContentClassName
          )}
          style={floatingOptions?.floatingContentStyles}
          /*
           * Prevent react synthetic events from bubbling up the react component tree. This can cause issues,
           * for example, with TableEditable which listens for and captures mouse events on table cells.
           *
           * For more details and context, see https://github.com/facebook/react/issues/11387
           */
          onMouseDown={
            floatingOptions?.eventPropagationOptions?.permitOnMouseDown ? undefined : stopPropagation
          }
          onDoubleClick={
            floatingOptions?.eventPropagationOptions?.permitOnDoubleClick ? undefined : stopPropagation
          }
          onContextMenu={
            floatingOptions?.eventPropagationOptions?.permitOnContextMenu ? undefined : stopPropagation
          }
          {...getTestProps(testAttrs)}
        >
          {/* Auto-focusing input to avoid tooltips opening on popover open */}
          {title && <Title>{title}</Title>}
          {renderContent({labelId, descriptionId, handleClose})}
        </div>
        {floatingOptions?.renderArrow != null && floatingOptions.renderArrow(arrowProps)}
      </div>
    </FloatingFocusManager>
  );
}

interface PresentationalFloatProps {
  arrowProps: ArrowProps;
  children: JSX.Element;
  floatingProps: FloatingProps;
  floatingElementOptions?: FloatingElementOptions;
  isOpen: boolean;
  renderContent: (data: RenderContentProps) => ReactNode;
  /**
   * ARIA role for the outermost floating element (i.e. the immediate child of the portal). If this float has
   * any interactivity within the floating content, this must be overridden.
   * @default tooltip
   */
  role?: string;
  strategy: Strategy;
  title?: string;
  toggleProps: Omit<ToggleProps, 'children'>;
  disableContentSelection?: boolean;
  stealFocus?: boolean;
}

/**
 * Reusable combination of `Toggle` and `FloatingElement` to encourage consistency between the different
 * interaction modes (which require separate component boundaries to manage a different set of hooks, a la the
 * rules of hooks).
 */
function PresentationalFloat({
  arrowProps,
  children,
  floatingProps,
  floatingElementOptions,
  isOpen,
  renderContent,
  role,
  strategy,
  title,
  toggleProps,
  disableContentSelection,
  stealFocus,
}: PresentationalFloatProps): JSX.Element {
  const {toggleRef, getReferenceProps, displayMode} = toggleProps;

  return (
    <>
      <Toggle
        displayMode={displayMode}
        floatingNodeId={floatingProps.floatingNodeId}
        getReferenceProps={getReferenceProps}
        toggleRef={toggleRef}
      >
        {children}
      </Toggle>
      <FloatingNode id={floatingProps.floatingNodeId}>
        {isOpen && (
          <FloatingPortal>
            <FloatingElement
              arrowProps={arrowProps}
              floatingProps={floatingProps}
              floatingOptions={floatingElementOptions}
              renderContent={renderContent}
              role={role}
              strategy={strategy}
              title={title}
              disableContentSelection={disableContentSelection}
              stealFocus={stealFocus}
            />
          </FloatingPortal>
        )}
      </FloatingNode>
    </>
  );
}

interface BaseFloatProps {
  /** Content which becomes the toggle element in the floating system. */
  children: JSX.Element;
  /** Direction in which to render floating content relative to the toggle element. The floating element is aware of
   * visible content boundaries and may alter its positioning automatically to become visible. Defaults to
   * TOP. */
  placement?: FloatingPlacement;
  /** Returns content to be displayed in the floating element */
  renderContent: (data: RenderContentProps) => ReactNode;
  /** Renders a Title element if present */
  title?: string;
  /**
   * Whether the element wrapping the toggle should be inline or block.
   *
   * @default ToggleDisplayMode.INLINE
   * */
  toggleDisplayMode?: ToggleDisplayMode;
  /** When true, will only render children, without Float */
  isDisabled?: boolean;
  /**
   * When closed, will return focus to whatever element had focus when the Floating element was opened.
   * This is true by default, and will be disabled if set false
   */
  returnFocusOnClose?: boolean;
  /**
   * ARIA role for the outermost floating element (i.e. the immediate child of the portal). If this float has
   * any interactivity within the floating content, this must be overridden.
   * @default tooltip
   */
  role?: HTMLAttributes<HTMLDivElement>['role'];
  /**
   * When true, the content of this floating element cannot be selected by the user.
   */
  disableContentSelection?: boolean;
  /**
   * Default true, the floating element will steal focus when opened.
   */
  stealFocus?: boolean;
  /**
   * When supplied, will overwrite the default test attrs for the floating content element.
   */
  dataTestAttrs?: DataTestAttrs;
}

export interface FloatOptions {
  floatingElementOptions?: FloatingElementOptions;
  placementOptions?: FloatingPlacementOptions;
}

export interface ControlledFloatProps extends BaseFloatProps, FloatOptions {
  isOpen: boolean;
  onRequestClose?: () => void;
  interaction: FloatingInteraction.CONTROLLED;
}

function ControlledFloat({
  children,
  floatingElementOptions,
  isOpen,
  onRequestClose,
  placement,
  placementOptions,
  renderContent,
  role,
  title,
  toggleDisplayMode,
  disableContentSelection = false,
  stealFocus,
}: ControlledFloatProps): JSX.Element {
  const {arrowProps, floatingProps, strategy, toggleRef} = useFloating({
    isOpen,
    setOpen: (isOpen) => {
      if (onRequestClose != null && !isOpen) {
        onRequestClose();
      }
    },
    placement,
    placementOptions,
  });

  const {getReferenceProps} = useInteractions([useDismiss(floatingProps.floatingContext)]);

  return (
    <PresentationalFloat
      arrowProps={arrowProps}
      floatingProps={floatingProps}
      floatingElementOptions={floatingElementOptions}
      isOpen={isOpen}
      renderContent={renderContent}
      role={role}
      strategy={strategy}
      title={title}
      toggleProps={{
        displayMode: toggleDisplayMode,
        floatingNodeId: floatingProps.floatingNodeId,
        getReferenceProps,
        toggleRef,
      }}
      disableContentSelection={disableContentSelection}
      stealFocus={stealFocus}
    >
      {children}
    </PresentationalFloat>
  );
}

export interface HoverFloatProps extends BaseFloatProps, FloatOptions {
  interaction?: FloatingInteraction.HOVER;
  /**
   * Time (in milliseconds) to wait to open/close the float. See https://floating-ui.com/docs/usehover#delay
   */
  delay?: {open: number; close: number};
  /**
   * Open the float once the cursor is at rest for this long (in milliseconds). Note that delay controls when
   * it will open if the cursor is not at rest.
   * See https://floating-ui.com/docs/usehover#restms
   */
  restMs?: number;
}

function HoverFloat({
  children,
  floatingElementOptions,
  placement,
  placementOptions,
  renderContent,
  role,
  title,
  toggleDisplayMode,
  delay = {open: 1000, close: 250},
  restMs = 40,
  disableContentSelection = false,
  stealFocus,
}: HoverFloatProps): JSX.Element {
  const [isOpen, setOpen] = useState(false);

  const {arrowProps, floatingProps, strategy, toggleRef} = useFloating({
    isOpen,
    setOpen,
    placement,
    placementOptions,
  });

  const {floatingContext} = floatingProps;

  const {getReferenceProps} = useInteractions([
    useHover(floatingContext, {
      delay,
      restMs,
      handleClose: safePolygon(),
    }),
    useRole(floatingContext),
    useDismiss(floatingContext),
  ]);

  return (
    <PresentationalFloat
      arrowProps={arrowProps}
      floatingProps={floatingProps}
      floatingElementOptions={floatingElementOptions}
      isOpen={isOpen}
      renderContent={renderContent}
      role={role}
      strategy={strategy}
      title={title}
      toggleProps={{
        displayMode: toggleDisplayMode,
        floatingNodeId: floatingProps.floatingNodeId,
        getReferenceProps,
        toggleRef,
      }}
      disableContentSelection={disableContentSelection}
      stealFocus={stealFocus}
    >
      {children}
    </PresentationalFloat>
  );
}

export interface ClickFloatProps extends BaseFloatProps, FloatOptions {
  interaction: FloatingInteraction.CLICK;
}

function ClickFloat({
  children,
  floatingElementOptions,
  placement,
  placementOptions,
  renderContent,
  role,
  title,
  toggleDisplayMode,
  disableContentSelection,
  stealFocus,
}: ClickFloatProps): JSX.Element {
  const [isOpen, setOpen] = useState(false);

  const {arrowProps, floatingProps, strategy, toggleRef} = useFloating({
    isOpen,
    setOpen,
    placement,
    placementOptions,
  });

  const {floatingContext} = floatingProps;

  const {getReferenceProps} = useInteractions([
    useClick(floatingContext),
    useRole(floatingContext),
    useDismiss(floatingContext),
  ]);

  return (
    <PresentationalFloat
      arrowProps={arrowProps}
      floatingProps={floatingProps}
      floatingElementOptions={floatingElementOptions}
      isOpen={isOpen}
      renderContent={renderContent}
      role={role}
      strategy={strategy}
      title={title}
      toggleProps={{
        displayMode: toggleDisplayMode,
        floatingNodeId: floatingProps.floatingNodeId,
        getReferenceProps,
        toggleRef,
      }}
      disableContentSelection={disableContentSelection}
      stealFocus={stealFocus}
    >
      {children}
    </PresentationalFloat>
  );
}

export type FloatProps = ControlledFloatProps | HoverFloatProps | ClickFloatProps;

function WrappedFloat(props: FloatProps): JSX.Element {
  const {
    children,
    renderContent,
    role,
    title,
    floatingElementOptions,
    placementOptions,
    toggleDisplayMode,
    disableContentSelection,
    stealFocus,
  } = props;
  const placement = props.placement ?? FloatingPlacement.TOP;

  if (props.interaction === FloatingInteraction.CONTROLLED) {
    return (
      <ControlledFloat
        interaction={props.interaction}
        floatingElementOptions={floatingElementOptions}
        isOpen={props.isOpen}
        onRequestClose={props.onRequestClose}
        placement={placement}
        placementOptions={placementOptions}
        renderContent={renderContent}
        role={role}
        title={title}
        toggleDisplayMode={toggleDisplayMode}
        disableContentSelection={disableContentSelection}
        stealFocus={stealFocus}
      >
        {children}
      </ControlledFloat>
    );
  } else if (props.interaction === FloatingInteraction.CLICK) {
    return (
      <ClickFloat
        interaction={props.interaction}
        floatingElementOptions={floatingElementOptions}
        placement={placement}
        placementOptions={placementOptions}
        renderContent={renderContent}
        role={role}
        title={title}
        disableContentSelection={disableContentSelection}
        stealFocus={stealFocus}
      >
        {children}
      </ClickFloat>
    );
  } else {
    const hoverProps =
      props.interaction === FloatingInteraction.HOVER || !props.interaction
        ? {delay: props.delay, restMs: props.restMs}
        : {};

    return (
      <HoverFloat
        interaction={props.interaction}
        floatingElementOptions={floatingElementOptions}
        placement={placement}
        placementOptions={placementOptions}
        renderContent={renderContent}
        role={role}
        title={title}
        toggleDisplayMode={toggleDisplayMode}
        disableContentSelection={disableContentSelection}
        stealFocus={stealFocus}
        {...hoverProps}
      >
        {children}
      </HoverFloat>
    );
  }
}

/**
 * Float is like Popover but without any opinions about floating content style (i.e. speech bubble border,
 * shadow, and arrow).
 */
export default function Float(props: FloatProps): JSX.Element {
  const floatingParentNodeId = useFloatingParentNodeId();

  if (props.isDisabled ?? false) {
    return props.children;
  }

  // Wrap in <FloatingTree> at the top-level, because `useFloating` should be called within a child
  // of <FloatingTree>.
  return floatingParentNodeId == null ? (
    <FloatingTree>
      <WrappedFloat {...props} />
    </FloatingTree>
  ) : (
    <WrappedFloat {...props} />
  );
}

const styles = StyleSheet.create({
  floatingBody: {
    textAlign: 'left',
    ':focus': {
      outline: 'none',
    },
  },
  floatingContent: {
    overflowY: 'auto',
  },
  // TODO(BNCH-97400): Remove this comment (but not these styles!)
  // Keep styles in sync with floating-ui.less
  floatingToggle: {
    wordBreak: 'break-all',
    maxWidth: '100%',
  },
  inlineToggle: {
    display: 'inline-block',
  },
  blockToggle: {
    display: 'block',
  },
  blockFitContentToggle: {
    display: 'block',
    width: 'fit-content',
  },
});
